import { Component, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from "@angular/forms";
import { InputTextModule } from "primeng/inputtext";
import { ButtonModule } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { ToastModule } from "primeng/toast";
import { FooterBannerComponent } from "../footer-banner/footer-banner.component";
import { AccordionModule } from "primeng/accordion";
import { ToastrService } from "ngx-toastr";
import { animate, style, transition, trigger } from "@angular/animations";
import { IWaitlistUserReq, WaitlistContactMethod, WaitlistLeadSourceEnum } from "../../../Utils/_models/users.model";
import { SelectModule } from "primeng/select";
import { Textarea } from 'primeng/textarea';
import { AdminService } from "../../../Utils/_services/admin/admin/admin.service";
import { NavBarComponent } from "../../../shared/nav-bar/nav-bar.component";
@Component({
  selector: "app-coming-soon",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    SelectModule ,
    InputTextModule,
    Textarea,
    ButtonModule,
    CheckboxModule,
    ToastModule,
    NavBarComponent,
    FooterBannerComponent,
    AccordionModule,
  ],
  animations: [
    trigger("slideToggle", [
      transition(":enter", [
        style({ height: 0, opacity: 0 }),
        animate("450ms ease-out", style({ height: "*", opacity: 1 })),
      ]),
      transition(":leave", [
        animate("450ms ease-in", style({ height: 0, opacity: 0 })),
      ]),
    ]),
  ],
  templateUrl: "./coming-soon.component.html",
  styleUrl: "./coming-soon.component.css",
})
export class ComingSoonComponent {
  constructor(
    private adminService: AdminService,
    private toastr: ToastrService,
    private fb: FormBuilder
  ) {}

  private readonly LEAD_SOURCE_LABELS: Record<WaitlistLeadSourceEnum, string> =
    {
      [WaitlistLeadSourceEnum.SocialMedia]: "Social Media",
      [WaitlistLeadSourceEnum.WordOfMouth]: "Word of Mouth",
      [WaitlistLeadSourceEnum.Website]: "Website",
      [WaitlistLeadSourceEnum.EmailNewsLetter]: "Email Newsletter",
      [WaitlistLeadSourceEnum.Events]: "Events",
      [WaitlistLeadSourceEnum.SearchEngines]: "Search Engines",
      [WaitlistLeadSourceEnum.Other]: "Other",
    };
  waitlistForm!: FormGroup;
  contactMethods: { label: string; value: number }[] = [];
  leadSources: { label: string; value: number }[] = [];
  isLoading = signal(false);

  ngOnInit(): void {
    this.contactMethods = Object.keys(WaitlistContactMethod)
      .filter(key => isNaN(Number(key)))
      .map(key => ({
        label: key,
        value: WaitlistContactMethod[key as keyof typeof WaitlistContactMethod],
      }));

    this.leadSources = Object.values(WaitlistLeadSourceEnum)
      .filter(value => typeof value === "number")
      .map(value => ({
        label: this.LEAD_SOURCE_LABELS[value as WaitlistLeadSourceEnum],
        value: value as number,
      }));
    this.initForm();
  }

  initForm(): void {
    this.waitlistForm = this.fb.group({
      showOptionalFields: [false],
      firstName: ["", [Validators.required, Validators.minLength(2)]],
      lastName: ["", [Validators.required, Validators.minLength(2)]],
      email: ["", [Validators.required, Validators.email]],
      phone: [""],
      preferredContactMethod: [null],
      leadSource: [null],
      specificQuestionsOrRequests: [""],
    });

    // Listen to checkbox changes
    this.waitlistForm
      .get("showOptionalFields")
      ?.valueChanges.subscribe(isChecked => {
        this.updateOptionalFieldValidators(isChecked);
      });
  }

  updateOptionalFieldValidators(isRequired: boolean): void {
    const phoneControl = this.waitlistForm.get("phone");
    const questionsOrRequests = this.waitlistForm.get("specificQuestionsOrRequests");
    const contactMethodControl = this.waitlistForm.get(
      "preferredContactMethod"
    );
    const leadSourceControl = this.waitlistForm.get("leadSource");

    if (isRequired) {
      // Add validators when checkbox is checked
      phoneControl?.setValidators([
        Validators.required,
        Validators.pattern(/^\d{10}$/),
      ]);
      questionsOrRequests?.setValidators([Validators.required]);
      contactMethodControl?.setValidators([Validators.required]);
      leadSourceControl?.setValidators([Validators.required]);
    } else {
      // Remove validators when checkbox is unchecked
      phoneControl?.clearValidators();
      phoneControl?.setValidators([Validators.pattern(/^\d{10}$/)]); // Keep pattern validation if value exists
      questionsOrRequests?.clearValidators();
      contactMethodControl?.clearValidators();
      leadSourceControl?.clearValidators();
    }

    // Update validity
    phoneControl?.updateValueAndValidity();
    contactMethodControl?.updateValueAndValidity();
    questionsOrRequests?.updateValueAndValidity();
    leadSourceControl?.updateValueAndValidity();
  }

  onSubmit(): void {
    if (this.isLoading()) return;

    if (this.waitlistForm.valid) {
      const formData = this.waitlistForm.value;
      const body: IWaitlistUserReq = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        isOptionalFieldEnabled: formData.showOptionalFields ? formData.showOptionalFields : false,

      };

      if (formData.showOptionalFields) {
        body.phone = formData.phone;
        body.specificQuestionsOrRequests = formData.specificQuestionsOrRequests;
        body.leadSource = formData.leadSource;
        body.preferredContactMethod= formData.preferredContactMethod;
      }

      this.isLoading.set(true);

      this.adminService.joinWaitlist(body).subscribe({
        next: (response) => {
          this.toastr.success(response.msg);
          setTimeout(() => {
            this.isLoading.set(false);
            this.waitlistForm.reset();
          }, 5000);
        },
        error: (response) => {
          this.isLoading.set(false);
          this.toastr.error(response.error.msg);
        },
      });
    } else {
      this.markFormGroupTouched(this.waitlistForm);
    }
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      formGroup.get(key)?.markAsTouched();
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.waitlistForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }
}
